import { type PropsWithChildren, useEffect, useRef, useState } from "react";
import {
  animate,
  motion,
  type Transition,
  useMotionValue,
  useTransform,
} from "motion/react";

// Round to whole device pixels. In a bottom-anchored sheet, animating the
// height moves the content up and down. WebKit snaps text to device pixels but
// draws boxes at fractional positions, so without this rounding the text
// jiggles inside its box while the height animates.
const roundToDevicePixel = (value: number) => {
  const dpr = typeof window === "undefined" ? 1 : window.devicePixelRatio || 1;
  return `${Math.round(value * dpr) / dpr}px`;
};

const SmoothHeightTransition = ({
  children,
  className,
  transition,
}: PropsWithChildren<{ className?: string; transition?: Transition }>) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const lastHeight = useRef<number | null>(null);
  // Leave the height as "auto" until the first measurement
  const [isMeasured, setIsMeasured] = useState(false);

  const height = useMotionValue(0);
  const opacity = useMotionValue(1);
  const roundedHeight = useTransform(height, roundToDevicePixel);

  useEffect(() => {
    const firstChild = containerRef.current?.firstElementChild;
    if (!firstChild) return;

    const measure = () => {
      // offsetHeight ignores absolutely positioned children, like components
      // that are animating out
      const next = (firstChild as HTMLElement).offsetHeight;

      if (next === lastHeight.current) return;

      const nextOpacity = next === 0 ? 0 : 1;

      if (lastHeight.current === null) {
        // Jump to the first height instead of animating. It's the height we're
        // already at, and animating would start from the rendered size, which
        // is too small while a modal's open animation scales it down. That
        // used to clip the content.
        height.jump(next);
        opacity.jump(nextOpacity);
      } else {
        const options = {
          duration: 0.3,
          ease: "easeInOut" as const,
          ...transition,
        };
        animate(height, next, options);
        animate(opacity, nextOpacity, options);
      }

      lastHeight.current = next;
      setIsMeasured(true);
    };

    // Also re-measure when the content resizes on its own, e.g. a child
    // collapses, since the children prop doesn't change then
    const observer = new ResizeObserver(measure);
    observer.observe(firstChild);
    measure();

    return () => observer.disconnect();
  }, [children, height, opacity, transition]);

  return (
    <motion.div
      ref={containerRef}
      style={{
        overflow: "hidden",
        height: isMeasured ? roundedHeight : "auto",
        opacity,
      }}
      className={className}
    >
      {children}
    </motion.div>
  );
};

export { SmoothHeightTransition };
